import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import HandshakeIcon from "@mui/icons-material/Handshake";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SchoolIcon from "@mui/icons-material/School";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Icon from "@mui/material/Icon";
import ProjectCyrenaeus from "../components/AboutUs/ProjectCyrenaeus";
import AboutUsItem from "../components/AboutUs/AboutUsItem";

const AboutUsListItem = (props) => {
  const theme = useTheme();

  return (
    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
      <ListItemIcon sx={{ minWidth: 38, mt: 0.5 }}>{props.icon}</ListItemIcon>
      <ListItemText
        primary={props.text}
        primaryTypographyProps={{
          color: theme.palette.primary.main,
          lineHeight: 1.65,
        }}
      />
    </ListItem>
  );
};

const references = [
  "Meara, J. G., Hagander, L., & Leather, A. J. (2014). Surgery and global health: a Lancet Commission. The Lancet, 383(9911), 12-13.",
  "Roa, L., Jumbam, D. T., Makasa, E., & Meara, J. G. (2019). Global surgery and the sustainable development goals. Journal of British Surgery, 106(2), e44-e52.",
  "Goodacre, T. E. (1986). Plastic surgery in a rural African hospital: spectrum and implications. Annals of the Royal College of Surgeons of England, 68(1), 42.",
  "Jamison, D. T., Breman, J. G., Measham, A. R., Alleyne, G., Claeson, M., Evans, D. B., ... & Musgrove, P. (Eds.). (2006). Disease control priorities in developing countries.",
  "Debas, H. T., Donkor, P., Gawande, A., Jamison, D. T., Kruk, M. E., & Mock, C. N. (Eds.). (2015). Disease control priorities, (Volume 1): essential surgery. World Bank Publications.",
  "Royal College of Surgeons of England Guideline. Ethical Principles of Working Overseas.",
  "BFIRST website, core values.",
];

const AboutUs = () => {
  const theme = useTheme();

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <ProjectCyrenaeus />

      <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 2, sm: 4 }, py: 7 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 2,
            mb: 5,
          }}
        >
          {[
            {
              icon: <MedicalServicesIcon />,
              value: "Direct care",
              label: "Reconstructive surgery for complex burns, trauma and disease.",
            },
            {
              icon: <SchoolIcon />,
              value: "Training",
              label: "Practical skills shared with local doctors and nurses.",
            },
            {
              icon: <HandshakeIcon />,
              value: "Partnership",
              label: "Long-term collaboration with host institutions.",
            },
          ].map((item) => (
            <Box
              key={item.value}
              sx={{
                bgcolor: "white",
                borderRadius: 2,
                p: 2.5,
                minHeight: { xs: 148, md: 174 },
                border: "1px solid rgba(6, 57, 112, 0.1)",
                boxShadow: "0 14px 38px rgba(6, 57, 112, 0.08)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
              }}
            >
              <Box sx={{ color: theme.palette.donate.secondary, mb: 1 }}>
                {item.icon}
              </Box>
              <Typography
                variant="h5"
                component="h2"
                sx={{ color: theme.palette.primary.main, fontWeight: 900 }}
              >
                {item.value}
              </Typography>
              <Typography sx={{ color: "text.secondary", mt: 0.75 }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            alignItems: "stretch",
            gap: 2.5,
          }}
        >
          <AboutUsItem
            name="The Need"
            content={
              <List>
                {[
                  <>
                    The lack of global surgical infrastructure in LMICs has led
                    to huge morbidity, mortality, and financial burdens for
                    individuals and economies.<sup>1, 2</sup>
                  </>,
                  "Global reconstructive surgery includes care for burns, hand trauma, complex soft tissue wounds, skin cancer, sarcoma and congenital malformations.",
                  <>
                    In LMICs the reconstructive surgical burden represents at
                    least 16% of the total surgical burden.<sup>3, 4</sup>
                  </>,
                  <>
                    Reconstructive procedures for trauma and congenital
                    anomalies were identified as essential surgical procedures by
                    the World Bank report in 2015.<sup>5</sup>
                  </>,
                  "Lacor Hospital has 450 beds and 6 operating theatres, providing maternity, emergency, paediatric, general surgical and orthopaedic care.",
                ].map((text, index) => (
                  <AboutUsListItem
                    key={index}
                    text={text}
                    icon={<HandshakeIcon sx={{ color: theme.palette.donate.secondary }} />}
                  />
                ))}
              </List>
            }
          />
          <AboutUsItem
            name="The Value"
            content={
              <List>
                {[
                  "Assist Lacor Hospital in developing plastic surgery activity through training and infrastructure.",
                  "Maintain regular communication and support around reconstructive surgery provision.",
                  "Build reciprocal trainee relationships through education, mentorship and shared global surgical understanding.",
                  <>
                    Align our work with ethical guidance from the Royal College
                    of Surgeons and BFIRST.<sup>6, 7</sup>
                  </>,
                ].map((text, index) => (
                  <AboutUsListItem
                    key={index}
                    text={text}
                    icon={
                      <VolunteerActivismIcon
                        sx={{ color: theme.palette.donate.secondary }}
                      />
                    }
                  />
                ))}
              </List>
            }
          />
        </Box>

        <Accordion
          sx={{
            mt: 3,
            borderRadius: 2,
            boxShadow: "0 12px 34px rgba(6, 57, 112, 0.08)",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="h5"
                component="h2"
              sx={{
                color: theme.palette.primary.main,
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontStyle: "italic",
              }}
            >
              References
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              {references.map((reference, index) => (
                <AboutUsListItem
                  key={reference}
                  text={reference}
                  icon={
                    <Icon
                      sx={{ color: theme.palette.primary.main, lineHeight: "1" }}
                    >
                      {index + 1}
                    </Icon>
                  }
                />
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default AboutUs;
